import { useState } from 'react';
import { X, Star, MapPin, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { ServiceType, SearchFilters } from '@/types';
import { SERVICE_TYPE_LABELS } from '@/types';
import { useTranslation } from 'react-i18next';

interface FilterPanelProps {
  filters: SearchFilters;
  onChange: (filters: SearchFilters) => void;
  onClose?: () => void;
  showMobileClose?: boolean;
}

export function FilterPanel({
  filters,
  onChange,
  onClose,
  showMobileClose = false,
}: FilterPanelProps) {
  const { t } = useTranslation('search');
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'serviceTypes',
    'rating',
    'distance',
  ]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const handleServiceTypeChange = (type: ServiceType) => {
    const current = filters.serviceTypes || [];
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onChange({ ...filters, serviceTypes: updated.length > 0 ? updated : undefined });
  };

  const handleRatingChange = (rating: number | undefined) => {
    onChange({ ...filters, minRating: rating });
  };

  const handleDistanceChange = (distance: number | undefined) => {
    onChange({ ...filters, maxDistance: distance });
  };

  const handleVerifiedChange = (verified: boolean | undefined) => {
    onChange({ ...filters, isVerified: verified });
  };

  const clearAllFilters = () => {
    onChange({
      query: filters.query,
      location: filters.location,
      latitude: filters.latitude,
      longitude: filters.longitude,
    });
  };

  const hasActiveFilters =
    (filters.serviceTypes && filters.serviceTypes.length > 0) ||
    filters.minRating ||
    filters.maxDistance ||
    filters.isVerified;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">{t('filters')}</h3>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              {t('clearAll')}
            </button>
          )}
          {showMobileClose && onClose && (
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Verified Only - Featured at top */}
      <div className="px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-primary-50">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.isVerified || false}
            onChange={(e) => handleVerifiedChange(e.target.checked || undefined)}
            className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <div>
              <span className="text-sm font-medium text-gray-900">{t('verifiedOnly')}</span>
              <p className="text-xs text-gray-500">{t('verifiedDescription', 'Licensed, insured & background checked')}</p>
            </div>
          </div>
        </label>
      </div>

      {/* Service Types */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => toggleSection('serviceTypes')}
          className="flex items-center justify-between w-full px-4 py-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">{t('serviceType')}</span>
          {expandedSections.includes('serviceTypes') ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.includes('serviceTypes') && (
          <div className="px-4 pb-4 space-y-2">
            {(Object.keys(SERVICE_TYPE_LABELS) as ServiceType[]).map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.serviceTypes?.includes(type) || false}
                  onChange={() => handleServiceTypeChange(type)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{SERVICE_TYPE_LABELS[type]}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Rating */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => toggleSection('rating')}
          className="flex items-center justify-between w-full px-4 py-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">{t('minimumRating')}</span>
          {expandedSections.includes('rating') ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.includes('rating') && (
          <div className="px-4 pb-4 space-y-2">
            {[undefined, 4, 3, 2].map((rating) => (
              <label key={rating ?? 'any'} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rating"
                  checked={filters.minRating === rating}
                  onChange={() => handleRatingChange(rating)}
                  className="border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  {rating ? (
                    <>
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      {rating}+ stars
                    </>
                  ) : (
                    t('anyRating')
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Distance */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => toggleSection('distance')}
          className="flex items-center justify-between w-full px-4 py-3 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">{t('maxDistance')}</span>
          {expandedSections.includes('distance') ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.includes('distance') && (
          <div className="px-4 pb-4 space-y-2">
            {[undefined, 10, 25, 50, 100].map((distance) => (
              <label key={distance ?? 'any'} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="distance"
                  checked={filters.maxDistance === distance}
                  onChange={() => handleDistanceChange(distance)}
                  className="border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {distance ? `Within ${distance} miles` : t('anyDistance')}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
